import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import agent from "../api/agentEntrez";
import { DBRecords, DbWithState, DbParams, ESearchResult } from "../models/db";

import { RootState } from "./configureStore";
// import convert from "xml-js";
import { parseDBRecordsFromDocJSON } from "../util/docSumJsonResponseConversion";
import { toast } from "react-toastify";
import {
  convertFirstCharToCaps,
  getRetStart,
  mergeSearcHistory,
  queryTermFormatter,
} from "../util/util";
import {
  getDBAxiosParams,
  getFinalQueryAxiosParams,
  initDBParams,
  initFilters,
  initFinalFormattedSearchTerm,
  initPaginationData,
  initQueryFields,
  initSearchHistory,
  saveSearchHistory,
} from "./storeHelper";
import { getCurrentDateTime } from "../util/dateHelper";

interface EntrezDBState {
  dbsLoaded: boolean;
  status: string;
  dbParams: DbParams;
  recentSelectedDBs: string[] | null;
}

// https://redux-toolkit.js.org/api/createEntityAdapter

// treating each db of entrez as single entity

// Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field
const dbAdapter = createEntityAdapter<DbWithState>({
  // selectId: (db) => db.dbname, //setting dbname as the id, since this will be unique
  sortComparer: (a, b) => a.dbname.localeCompare(b.dbname),
});

//To fetch all the db names.
//last {state:RootState} is to specify the type of the state
export const fetchDbsAsync = createAsyncThunk<
  DbWithState[],
  void,
  { state: RootState }
>("entrez/fetchDBsAsync", async (_, thunkAPI) => {
  const params = getDBAxiosParams(thunkAPI.getState().entrezDb.dbParams, null);

  try {
    const response = await agent.Entrez.getDBs(params);

    const dbs = response.einforesult.dblist.map((name: string) =>
      Object.assign(
        {},
        {
          id: name,
          dbname: name,
          displayname: convertFirstCharToCaps(name),

          //extra state for application. Initialized to default state.
          queryFields: initQueryFields(name),
          finalFormattedSearchTerm: initFinalFormattedSearchTerm(name),
          isReadyForSearch: false,
          eSearchResult: null,
          isReadyForFetch: false,
          dbRecords: null,
          selectedColumns: [],
          paginationData: initPaginationData(),
          filters: initFilters(),
          searchHistory: initSearchHistory(name),
        }
      )
    );

    return dbs;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});

//To fetch all the fields for selected db
export const fetchSelectedDbDetailsAsync = createAsyncThunk<
  DbWithState,
  void,
  { state: RootState }
>("entrez/fetchSelectedDbDetailsAsync", async (_, thunkAPI) => {
  const dbParams = thunkAPI.getState().entrezDb.dbParams;
  const params = getDBAxiosParams(dbParams, null);

  try {
    if (!dbParams.db) {
      console.log("db name is not set");
      throw new Error("db name is not set. can't hit the API");
    } else console.log(dbParams);

    const entities = thunkAPI.getState().entrezDb.entities;
    if (!entities) {
      console.log("entities are not set");
      throw new Error("entities are not set, can't perform ESearch");
    }

    //hit the server to get the selected db details.
    const response = await agent.Entrez.getDBs(params);

    //merge the response with existing state
    var selectedDBState = entities[dbParams.db];

    var updatedState = {
      id: response.einforesult.dbinfo[0].dbname,
      dbname: response.einforesult.dbinfo[0].dbname,
      displayname: convertFirstCharToCaps(
        response.einforesult.dbinfo[0].dbname
      ),
      menuname: response.einforesult.dbinfo[0].menuname,
      description: response.einforesult.dbinfo[0].description,
      count: response.einforesult.dbinfo[0].count,
      lastupdate: response.einforesult.dbinfo[0].lastupdate,
      fieldlist: response.einforesult.dbinfo[0].fieldlist,
    };

    const db_with_fields: DbWithState = {
      ...selectedDBState!,
      ...updatedState,
    };

    return db_with_fields;

    //we calling following to extract metadata object which API passes along with products
    //thunkAPI.dispatch(setMetaData(response.metaData));
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});

//perform the eSearch and setup the webenv. In return we get query key and webenv id
//first paramater SearchResult is return type and next paramter is input to function.
export const fetchESearchResultsAsync = createAsyncThunk<
  ESearchResult,
  void,
  { state: RootState }
>("entrez/fetchESearchResultsAsync", async (_, thunkAPI) => {
  const dbParams = thunkAPI.getState().entrezDb.dbParams;
  const entities = thunkAPI.getState().entrezDb.entities;

  try {
    if (!dbParams.db) {
      console.log("db name is not set");
      throw new Error("db name is not set. cant hit the entrez db");
    } else console.log(dbParams);

    if (!entities) {
      console.log("entities are not set");
      throw new Error("entities are not set, can't perform ESearch");
    }

    console.log("Current state of entities are:");
    console.log(entities);

    const selectedDBState = entities[dbParams.db];

    const params = getDBAxiosParams(dbParams, selectedDBState!.filters);

    const response = await agent.Entrez.getSearchResults(params);

    console.log(response.esearchresult);
    return response.esearchresult;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});

//here Operation type (i.e summary or detail) is input to function.
//this one to retrieve the details for UIDs or saved web environment
export const fetchESummaryOrEFetchAsync = createAsyncThunk<
  DBRecords,
  string,
  { state: RootState }
>("entrez/fetchESummaryOrEFetchAsync", async (opType, thunkAPI) => {
  const dbParams = thunkAPI.getState().entrezDb.dbParams;
  const entities = thunkAPI.getState().entrezDb.entities;

  try {
    if (!entities) {
      console.log("entities are not set");
      throw new Error("entities are not set, can't perform ESearch");
    }

    const selectedDBState = entities[dbParams.db];
    console.log(selectedDBState);

    var finalQueryParams = {
      db: dbParams.db,
      querykey: selectedDBState!.eSearchResult!.querykey,
      webenv: selectedDBState!.eSearchResult!.webenv,
      retmax: selectedDBState!.paginationData!.pageSize,
      retstart: getRetStart(
        selectedDBState!.paginationData.currentPage,
        selectedDBState!.paginationData.pageSize
      ),
      retmode: "json",
      rettype: "docsum",
    };

    console.log(finalQueryParams);

    const params = getFinalQueryAxiosParams(finalQueryParams);

    var response;
    if (opType === "Summary") {
      response = await agent.Entrez.getSearchResultSummary(params);
    } else {
      response = await agent.Entrez.getSearchResultDetails(params);
    }

    // console.log("fetchESummaryOrEFetchAsync");
    // var str = response.toString();//JSON.stringify(response, null, 2); // spacing level = 2

    // var xmlToJsonResult = convert.xml2json(response.toString(), {
    //   compact: true,
    //   spaces: 4,
    // });

    //JSON.parse(response)
    var result = parseDBRecordsFromDocJSON(response.result);
    //console.log(result);
    return result;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.data });
  }
});

export const entrezDBSlice = createSlice({
  name: "entrezDB",
  initialState: dbAdapter.getInitialState<EntrezDBState>({
    dbsLoaded: false,
    status: "idle",
    dbParams: initDBParams(),
    recentSelectedDBs: null,
  }),

  reducers: {
    setDbParams: (state, action) => {
      // state.dbsLoaded = false;
      console.log("setDBParams called");
      state.dbParams = {
        ...state.dbParams,
        ...action.payload,
      };

      console.log(state.dbParams);
    },

    //main method to update object state
    // { id: 'a', changes: { title: 'First (altered)' } }
    updateSelectedDBState: (state, action) => {
      dbAdapter.updateOne(state, action.payload);
    },

    setRecentlySelecteDBS: (state, action) => {
      if (state.recentSelectedDBs === null) {
        state.recentSelectedDBs = [action.payload.db];
      } else {
        if (!state.recentSelectedDBs.includes(action.payload.db)) {
          if (state.recentSelectedDBs.length >= 5) {
            state.recentSelectedDBs.pop();
            state.recentSelectedDBs.unshift(action.payload.db);
          } else {
            state.recentSelectedDBs.unshift(action.payload.db);
          }
        }
      }
    },
  },

  //
  extraReducers: (builder) => {
    //1. For all db names
    builder.addCase(fetchDbsAsync.pending, (state) => {
      state.status = "pendingFetchDBs";
    });

    builder.addCase(fetchDbsAsync.fulfilled, (state, action) => {
      dbAdapter.setAll(state, action.payload);
      state.status = "idle";
      state.dbsLoaded = true;
    });

    builder.addCase(fetchDbsAsync.rejected, (state, action) => {
      console.log(action);
      state.status = "idle";
    });

    //2. For selected db details - its description and fields.
    builder.addCase(fetchSelectedDbDetailsAsync.pending, (state) => {
      state.status = "pendingFetchDBDetails";
    });

    builder.addCase(fetchSelectedDbDetailsAsync.fulfilled, (state, action) => {
      // accepts a single entity. If an entity with that ID exists, it will perform a shallow update
      // and the specified fields will be merged into the existing entity,
      // with any matching fields overwriting the existing values. If the entity does not exist, it will be added.
      dbAdapter.upsertOne(state, action.payload);
      state.status = "idle";
    });

    builder.addCase(fetchSelectedDbDetailsAsync.rejected, (state, action) => {
      console.log(action);
      state.status = "idle";
    });

    //3. For esearch results.
    builder.addCase(fetchESearchResultsAsync.pending, (state) => {
      state.status = "pendingEFetchSearchResults";
    });

    builder.addCase(fetchESearchResultsAsync.fulfilled, (state, action) => {
      var selectedDBState = state.entities[state.dbParams.db];
      var eSearchResult = action.payload;

      //construct PaginationData object from eSearchResult response
      const updatedPaginationData = {
        currentPage: 1,
        totalCount: eSearchResult == null ? 0 : eSearchResult.count,
        totalPages:
          eSearchResult == null
            ? 0
            : Math.ceil(
                eSearchResult.count / selectedDBState!.paginationData.pageSize!
              ),
      };

      // var meta=Object.assign(selectedDBState!.metaData)

      //create the history object
      var newHistory = {
        queryTerms: selectedDBState!.queryFields,
        formattedSearchTerm: queryTermFormatter(selectedDBState!.queryFields),
        count: eSearchResult == null ? 0 : eSearchResult.count,
        timeStamp: getCurrentDateTime(),
      };

      var lastHistory = selectedDBState!.searchHistory;
      var newMergedHistory = mergeSearcHistory(lastHistory, newHistory);

      // { id: 'a', changes: { title: 'First (altered)' } }
      var updatedObj = {
        id: state.dbParams.db,
        changes: {
          eSearchResult: action.payload,
          paginationData: {
            ...selectedDBState!.paginationData,
            ...updatedPaginationData,
          },
          searchHistory: newMergedHistory,
          isReadyForSearch: false, //once search is performed set it to false.
          isReadyForFetch: true, //setting this to true, this one is used in page to trigger useEffect to load dbrecords
        },
      };

      //update the selectedDb state
      dbAdapter.updateOne(state, updatedObj);

      //update local storage for history
      saveSearchHistory(state.dbParams.db, newMergedHistory);

      state.status = "idle";
      console.log("add case of fetchesearchresult");
    });

    builder.addCase(fetchESearchResultsAsync.rejected, (state, action) => {
      console.log(action);

      var updatedObj = {
        id: state.dbParams.db,
        changes: {
          eSearchResult: null,
          isReadyForSearch: false,
          isReadyForFetch: false,
        },
      };

      dbAdapter.updateOne(state, updatedObj);

      state.status = "idle";
    });

    //4. For ESummary or EFetch:
    builder.addCase(fetchESummaryOrEFetchAsync.pending, (state) => {
      state.status = "pendingEFetchESummaryOrEFetchAsync"; //+ state.resultQueryParams.id;
    });

    builder.addCase(fetchESummaryOrEFetchAsync.fulfilled, (state, action) => {
      var selectedDBState = state.entities[state.dbParams.db];
      selectedDBState!.dbRecords = action.payload;
      selectedDBState!.isReadyForFetch = false; // turn the switch so that no more fetch again from the useEffect of component
      state.status = "idle";
      toast.success("Search Completed");
    });

    builder.addCase(fetchESummaryOrEFetchAsync.rejected, (state, action) => {
      console.log(action);
      state.status = "idle";
      var selectedDBState = state.entities[state.dbParams.db];
      selectedDBState!.isReadyForFetch = false;
      toast.error("Search failed");
    });
  },
});

//The getSelectors method returns five selectors, most important of which, and pretty self-explanatory, are selectAll and selectById.
export const entrezDbSelectors = dbAdapter.getSelectors(
  (state: RootState) => state.entrezDb
);

export const { setDbParams, updateSelectedDBState, setRecentlySelecteDBS } =
  entrezDBSlice.actions;
